/**
 * lib/labels.ts — the faces a record can show in its middle: twelve labels, and the track's
 * cover; and the adapter in the middle of the 45 that suits each.
 *
 * `spec.md` «Каталог и данные», «Этикетка». The owner chose the twelve on 2026-09-29 and 30 from
 * three galleries (`.omc/wiki/raw/prototip-vertushki.html`, `…/etiketki-2026-09-29.html`,
 * `…/etiketki-i-vkladyshi.html`): «Ямайская», which the app had carried alone since task 24;
 * «Саундсистема», «Три кольца» and «Индастриал» from the first; «Студия», «Рутс» and «Закат» from
 * the second; «Ночной пляж», «Полдень», «Тропик», «Остров» and «Золотая серия» from the third.
 * Each is a ready picture per record (`assets/label-<kind>-33.svg`, `…-45.svg`); on the five paper
 * ones the track's title and performer are written (`lib/label.ts`, `components/RecordLabel.tsx`).
 *
 * ## The adapter each label was chosen with
 *
 * The 45's big hole is filled by an adapter, and until 2026-09-30 it was always the flag. The
 * third gallery gave its labels adapters of their own, and the owner sent the five he liked as it
 * drew them — the chrome under «Ночной пляж», the spider under «Тропик», the palm under «Остров»,
 * the bolt under «Золотая серия», the flag under «Полдень». The seven he had chosen before, he
 * chose with the flag; when the gallery gave them other adapters he said the earlier ones had been
 * good (`.omc/wiki/varianty-etiketok-i-vkladyshey.md`). So each label keeps the adapter it was
 * chosen with — {@link adapterFor} — and the cover keeps the flag.
 *
 * ## One more face: the cover
 *
 * A track with a cover of its own has it as an eighth face, drawn like the others — the owner
 * wanted the labels to change on every record, and a cover that always won would have kept them
 * off most of the catalogue. A face that is the cover on a track that has none shows «Ямайская»,
 * as a missing cover did before (`components/Cover.tsx`, `fallback`).
 *
 * ## The draw
 *
 * Random when the deck opens, and random again at each tap, never the face already on the
 * record: a tap that changed nothing would look like a tap that was not heard. Pure — the draw
 * is a function, `Math.random` by default, so a test gives it a number.
 */

/** The labels, in the order the owner met them. */
export const LABEL_KINDS = [
  'jamaican',
  'soundsystem',
  'rings',
  'industrial',
  'studio',
  'roots',
  'sunset',
  'night',
  'noon',
  'tropic',
  'isle',
  'gold',
] as const;

export type LabelKind = (typeof LABEL_KINDS)[number];

/** What the middle of the record shows: a label, or the track's own cover. */
export type RecordFace = LabelKind | 'cover';

/** The labels the track's title and performer are written on: the paper ones. */
export const WITH_LINES: ReadonlySet<LabelKind> = new Set<LabelKind>([
  'jamaican',
  'studio',
  'tropic',
  'isle',
  'gold',
]);

/** The adapters of the 45 (`assets/adapter-<kind>.svg`). */
export const ADAPTER_KINDS = ['flag', 'chrome', 'spider', 'palm', 'bolt'] as const;

export type AdapterKind = (typeof ADAPTER_KINDS)[number];

/** Each label's adapter: the one it was chosen with. */
const ADAPTER_OF: Readonly<Record<LabelKind, AdapterKind>> = {
  jamaican: 'flag',
  soundsystem: 'flag',
  rings: 'flag',
  industrial: 'flag',
  studio: 'flag',
  roots: 'flag',
  sunset: 'flag',
  night: 'chrome',
  noon: 'flag',
  tropic: 'spider',
  isle: 'palm',
  gold: 'bolt',
};

/** The adapter in the middle of a 45 whose face is `face`; the cover keeps the flag. */
export function adapterFor(face: RecordFace): AdapterKind {
  return face === 'cover' ? 'flag' : ADAPTER_OF[face];
}

/**
 * A face for the record: any of the pool but `current` — the labels, and the cover when the
 * track has one. `draw` returns a number in [0, 1), as `Math.random` does.
 */
export function nextFace(
  hasCover: boolean,
  current: RecordFace | undefined,
  draw: () => number = Math.random,
): RecordFace {
  const pool: RecordFace[] = hasCover ? [...LABEL_KINDS, 'cover'] : [...LABEL_KINDS];
  const choices = pool.filter((face) => face !== current);
  const at = Math.min(choices.length - 1, Math.max(0, Math.floor(draw() * choices.length)));
  return choices[at] ?? 'jamaican';
}
